import { DropzoneFlexDirectionSettings, FlexDirection, IDropzoneFile } from '@common/types/common';
import { FileDropzoneArea, FileDropzoneAreaProps } from '@components/utility/files';
import { ErrorFrame } from '@components/utility/forms';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { alpha, Divider, IconButton, IconButtonProps, Stack, StackProps, Typography } from '@mui/material';
import { toaster } from '@utils/common';
import clsx, { ClassValue } from 'clsx';
import { FormikProps, FormikValues, useFormikContext } from 'formik';
import { ReactElement, useMemo } from 'react';

type FileUploadProps<F extends IDropzoneFile = IDropzoneFile, T extends object = FormikValues> = {
    fileFieldName: keyof T;
    formik: FormikProps<T>;
    fileDropzoneAreaProps?: Omit<FileDropzoneAreaProps<F>, 'onDrop'>;
    flexDirection?: DropzoneFlexDirectionSettings;
    containerClassName?: ClassValue;
    maxFiles?: number;
    uploadFileIcon?: ReactElement;
    resetOnDrop?: boolean;
    keepOnError?: boolean;
    fileListProps?: Omit<FileListProps, 'fileList' | 'removeFile'>;
    error?: boolean;
    helperText?: React.ReactNode;
    disabled?: boolean;
};

type FileListProps = StackProps & {
    fileList: IDropzoneFile[];
    removeFile: (index: number) => void;
    uploadFileIcon?: ReactElement;
    deleteIcon?: IconButtonProps;
};

const FileList = ({
    fileList,
    removeFile,
    uploadFileIcon,
    deleteIcon,
    disabled,
    ...props
}: FileListProps & { disabled?: boolean }) => {
    const getFileSize = (file: File) => {
        let size = file.size;
        const fSExt = ['Bytes', 'KB', 'MB', 'GB'];
        let i = 0;
        while (size > 900) {
            size /= 1024;
            i++;
        }
        return `${Math.round(size * 100) / 100} ${fSExt[i]}`;
    };

    return (
        <Stack
            direction="column"
            width="100%"
            height="fit-content"
            gap={2}
            className="truncate"
            divider={<Divider />}
            {...props}
        >
            {fileList.length > 0 &&
                fileList.map((fileInfo, i) => {
                    return (
                        <div
                            id={`file-${fileInfo.file.name}-${fileInfo.file.size}`}
                            key={`${fileInfo.file.name}-${fileInfo.file.size}`}
                            className="w-full"
                        >
                            <div className="flex items-center gap-2">
                                {uploadFileIcon ?? <UploadFileIcon color="primary" className="mr-2" />}
                                <div className="flex flex-col gap-1 truncate">
                                    <Typography color="neutral-800" fontSize={'medium'} className="truncate">
                                        {fileInfo.file.name}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: (theme) => alpha(theme.palette.black.main, 0.6) }}
                                        className="flex gap-2.5 items-center truncate"
                                    >
                                        <span>{getFileSize(fileInfo.file)}</span>
                                    </Typography>
                                </div>
                                <div className="ml-auto flex items-center">
                                    <IconButton {...deleteIcon} onClick={() => removeFile(i)} disabled={disabled}>
                                        {deleteIcon?.children ?? <DeleteIcon />}
                                    </IconButton>
                                </div>
                            </div>
                            {fileInfo.error && (
                                <div className="w-full flex py-2.5 px-3 gap-4 bg-jasper rounded-lg my-5">
                                    <ErrorOutlineIcon color="white" />
                                    <div className="flex flex-col gap-1">
                                        <Typography variant="body1" color="whitesmoke" fontWeight={600}>
                                            Upload Failed
                                        </Typography>
                                        <Typography color="whitesmoke" fontWeight={500} variant="body2">
                                            {fileInfo.error}
                                        </Typography>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
        </Stack>
    );
};

const removeFile = <F extends IDropzoneFile>({
    fileList,
    index,
    setFieldValue,
    handleChange,
    fileFieldName,
}: {
    fileList: F[];
    index: number;
    setFieldValue?: (field: string, value: any) => void;
    handleChange?: (e: React.ChangeEvent<any>) => void;
    fileFieldName: string;
}) => {
    const updatedFiles = [...fileList];
    updatedFiles.splice(index, 1);
    setFieldValue?.(fileFieldName as string, updatedFiles);
    handleChange?.({ target: { name: fileFieldName, value: updatedFiles } } as any);
};

const handleDrop = <F extends IDropzoneFile>({
    newFiles,
    resetOnDrop,
    fileList,
    maxFiles,
    keepOnError,
    setFieldValue,
    handleChange,
    fileFieldName,
}: {
    newFiles: F[];
    resetOnDrop?: boolean;
    fileList: F[];
    maxFiles?: number;
    keepOnError?: boolean;
    setFieldValue?: (field: string, value: any) => void;
    handleChange?: (e: React.ChangeEvent<any>) => void;
    fileFieldName: string;
}) => {
    if (!resetOnDrop) {
        newFiles = newFiles.map((nf) => {
            if (fileList.some((fle) => fle.file.name === nf.file.name)) {
                nf.error = 'Duplicate File';
            }

            return nf;
        });
    }

    const newFileList = !resetOnDrop
        ? [...fileList, ...newFiles.filter((nf) => !nf.error)]
        : newFiles.filter((nf) => !nf.error);
    const filesWithErrors = newFiles.filter((nf) => !!nf.error).map((fwe) => fwe.file.name);

    if (maxFiles && newFileList.length > maxFiles) {
        toaster.failure({
            msg: `The maximum number of files is ${maxFiles}`,
            title: 'File limit reached',
        });
        return;
    } else {
        if (newFileList.length === 0 && keepOnError) {
            return;
        }
        setFieldValue?.(fileFieldName as string, newFileList);
        handleChange?.({ target: { name: fileFieldName, value: newFileList } } as any);
    }

    if (filesWithErrors.length > 0) {
        toaster.failure({
            msg: (
                <>
                    Files:
                    <ul>
                        {filesWithErrors.map((f) => {
                            return <li key={f}>{f}</li>;
                        })}
                    </ul>
                </>
            ),
            title: `${filesWithErrors.length} files were not added because they're duplicates.`,
        });
    }
};

const Standard = <F extends IDropzoneFile, T extends object = FormikValues>({
    fileFieldName,
    formik: { values, setFieldValue },
    fileDropzoneAreaProps,
    flexDirection = {
        default: FlexDirection.COL,
        md: FlexDirection.ROW,
    },
    containerClassName,
    maxFiles,
    uploadFileIcon,
    resetOnDrop = true,
    keepOnError = true,
    fileListProps,
}: FileUploadProps<F, T>) => {
    const fileList = useMemo(() => (values[fileFieldName] as F[]) || [], [values, fileFieldName]);

    const handleDropHandler = (newFiles: F[]) => {
        handleDrop({
            newFiles,
            resetOnDrop,
            fileList,
            maxFiles,
            keepOnError,
            setFieldValue,
            fileFieldName: fileFieldName as string,
        });
    };

    const removeFileHandler = (index: number) => {
        removeFile({ fileList, index, setFieldValue, fileFieldName: fileFieldName as string });
    };

    return (
        <div
            className={clsx(`flex flex-${flexDirection.default} md:flex-${flexDirection.md} gap-6`, containerClassName)}
        >
            <FileDropzoneArea<F>
                {...fileDropzoneAreaProps}
                maxFiles={maxFiles}
                containerProps={{
                    ...fileDropzoneAreaProps?.containerProps,
                    className: clsx('w-full', fileDropzoneAreaProps?.containerProps?.className),
                }}
                onDrop={handleDropHandler}
            />
            <div className="w-full h-full max-h-40 overflow-y-auto px-4 pb-9 md:max-h-80 md:min-h-28 md:py-4 md:pr-4">
                <FileList
                    fileList={fileList}
                    removeFile={removeFileHandler}
                    uploadFileIcon={uploadFileIcon}
                    {...fileListProps}
                />
            </div>
        </div>
    );
};

const Vertical = <T extends object = FormikValues>({
    fileDropzoneAreaProps,
    maxFiles,
    resetOnDrop,
    fileFieldName,
    keepOnError,
    fileListProps,
    error,
    helperText,
    disabled,
}: FileUploadProps<IDropzoneFile, T>) => {
    const formik = useFormikContext<T>();
    const { values, setFieldValue, setFieldTouched } = formik;
    const fileList = useMemo(() => (values[fileFieldName] as IDropzoneFile[]) || [], [values, fileFieldName]);

    const handleRemoveFile = async (index: number) => {
        removeFile({ fileList, index, setFieldValue, fileFieldName: fileFieldName as string });
        setFieldTouched(fileFieldName as string);
        // Validate after state updates
        setTimeout(() => {
            formik.validateForm();
        }, 0);
    };

    return (
        <>
            <ErrorFrame error={error} helperText={helperText} showHelperText>
                <FileDropzoneArea
                    {...fileDropzoneAreaProps}
                    maxFiles={maxFiles}
                    onDrop={(newFiles) =>
                        handleDrop({
                            newFiles,
                            resetOnDrop,
                            fileList,
                            maxFiles,
                            keepOnError,
                            setFieldValue,
                            fileFieldName: fileFieldName as string,
                        })
                    }
                    fileFieldName={fileFieldName as string}
                    disabled={disabled}
                />
            </ErrorFrame>
            <FileList {...fileListProps} fileList={fileList} removeFile={handleRemoveFile} disabled={disabled} />
        </>
    );
};

/**
 * This must be used within Formik
 */
export const FileUploadNamespace = Object.assign(Standard, {
    Standard,
    Vertical,
});

export { FileUploadNamespace as FileUpload };
export type { FileUploadProps };
